import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { MicIcon } from '@/components/icons';

import { anamClient } from './anamClient';
import { useAvatarSession } from './useAvatarSession';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';

/** While held, refresh activity often enough to outpace a 60s idle timer. */
const HOLD_ACTIVITY_INTERVAL_MS = 10_000;

interface PushToTalkButtonProps {
  /** Disable while the avatar session isn't ready yet. */
  disabled?: boolean;
  /** Render a smaller diameter for overlay placement. */
  compact?: boolean;
  /** Optional activity ping (e.g. kiosk idle reset) on press and while held. */
  onActivity?: () => void;
}

/**
 * Hold-to-Talk button for the Anam avatar.
 *
 * Pointer-down interrupts any in-flight persona speech (so the user doesn't
 * have to wait for it to finish before being heard) and unmutes Anam's
 * input audio, which begins streaming the user's mic into Anam's
 * persona-side STT. Pointer-up re-mutes the mic. The finalized transcript
 * is delivered asynchronously via the MESSAGE_HISTORY_UPDATED event and
 * surfaced through `anamClient.onUserTranscript` (wired in `ChatBot`).
 *
 * Pointer events instead of mousedown/touchstart so a single handler
 * covers mouse, touch, and stylus without the duplicate-event dance.
 */
export function PushToTalkButton({
  disabled = false,
  compact = false,
  onActivity,
}: PushToTalkButtonProps) {
  const [listening, setListening] = React.useState(false);
  const { t, locale } = useWidgetI18n();
  const { touchAvatarActivity } = useAvatarSession();

  const beginListening = React.useCallback(() => {
    touchAvatarActivity();
    onActivity?.();
    anamClient.interrupt();
    anamClient.setMicEnabled(true);
    setListening(true);
  }, [touchAvatarActivity, onActivity]);

  const handlePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (e.button !== 0) return;
      // Capture so we still get the up/cancel events even if the pointer
      // drifts off the button while held.
      e.currentTarget.setPointerCapture(e.pointerId);
      beginListening();
    },
    [beginListening, disabled]
  );

  const handlePointerEnd = React.useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // Already released — safe to ignore.
      }
      anamClient.setMicEnabled(false);
      setListening(false);
    },
    []
  );

  // Safety net: if the component unmounts while held (e.g. avatar mode is
  // toggled off mid-press), make sure we don't leave the mic hot.
  React.useEffect(() => {
    return () => {
      if (listening) anamClient.setMicEnabled(false);
    };
  }, [listening]);

  // Keep external idle timers alive for long holds (pointer stays down).
  React.useEffect(() => {
    if (!listening) return undefined;
    const id = setInterval(() => onActivity?.(), HOLD_ACTIVITY_INTERVAL_MS);
    return () => clearInterval(id);
  }, [listening, onActivity]);

  const ariaLabel = listening
    ? t('avatar.releaseToSend')
    : t('avatar.holdToTalk');
  const size = compact ? 40 : 56;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, userSelect: 'none' }}>
      <span>
        <IconButton
          lang={locale}
          aria-label={ariaLabel}
          aria-pressed={listening}
          disabled={disabled}
          onKeyDown={(e) => {
            if (disabled) return;
            if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
              e.preventDefault();
              beginListening();
            }
          }}
          onKeyUp={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              anamClient.setMicEnabled(false);
              setListening(false);
            }
          }}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          onPointerLeave={listening ? handlePointerEnd : undefined}
          onContextMenu={(e) => e.preventDefault()}
          sx={{
            width: size,
            height: size,
            bgcolor: listening ? 'error.main' : 'primary.main',
            color: listening ? 'error.contrastText' : 'primary.contrastText',
            boxShadow: 3,
            transition: 'transform 0.1s ease, background-color 0.15s ease',
            transform: listening ? 'scale(1.1)' : 'scale(1)',
            '&:hover': { bgcolor: listening ? 'error.dark' : 'primary.dark' },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
          }}
        >
          <MicIcon fontSize={compact ? 'small' : 'medium'} />
        </IconButton>
      </span>
      {!compact && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, minHeight: 16 }}>
          {disabled ? 'Avatar offline' : listening ? 'Listening…' : 'Hold to talk'}
        </Typography>
      )}
    </Box>
  );
}
